<script setup lang="ts">
import { computed } from "vue";

import actions from "~/actions";
import EmptyState from "~/components/dumb/EmptyState.vue";
import { TASK_CONTEXT_MENU_OPTIONS } from "~/components/visualization/constants";
import { RecentIcon } from "~/icons";
import { EditorMode } from "~/shared/enums";
import { useAppStore, useDataStore, usePageStore, useTenantStore } from "~/stores";

import HomeTaskRow from "./HomeTaskRow.vue";
import RecentWidgetDocItem from "./RecentWidgetDocItem.vue";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const recentTasks = computed(() => appStore.recentTaskStack.slice(0, 10));
const recentDocs = computed(() => appStore.recentDocStack.slice(0, 10));

const areRecentTasks = computed(() => recentTasks.value.length > 0);
const areRecentDocs = computed(() => recentDocs.value.length > 0);

const onDocContextMenu = async (event: MouseEvent, duid: string) => {
  const doc = await dataStore.getDocByDuid(duid);
  if (!doc || (tenantStore.isDart && !pageStore.adminHidden && event.altKey)) {
    return;
  }

  appStore.openContextMenu(event as PointerEvent, actions.context.doc(doc));
};

const onTaskContextMenu = async (event: MouseEvent, duid: string) => {
  const task = await dataStore.getTaskByDuid(duid);
  if (!task || (tenantStore.isDart && !pageStore.adminHidden && event.altKey)) {
    return;
  }

  appStore.openContextMenu(
    event as PointerEvent,
    actions.context.task(task, EditorMode.CONTEXT_MENU, [task], false),
    TASK_CONTEXT_MENU_OPTIONS
  );
};
</script>

<template>
  <div class="h-80 w-full flex-col rounded-xl py-4 pl-4 bg-lt">
    <div v-if="areRecentTasks || areRecentDocs" class="flex size-full flex-col gap-5 overflow-y-scroll pr-px">
      <!-- Tasks -->
      <div v-if="areRecentTasks" class="flex flex-col gap-3">
        <span class="select-none truncate text-sm/[18px] text-lt">Tasks</span>
        <div class="flex flex-col gap-2">
          <HomeTaskRow
            v-for="duid in recentTasks"
            :key="duid"
            :duid="duid"
            @context-menu="(event: MouseEvent) => onTaskContextMenu(event, duid)" />
        </div>
      </div>

      <!-- Docs -->
      <div v-if="areRecentDocs" class="flex flex-col gap-3">
        <span class="select-none truncate text-sm/[18px] text-lt">Docs</span>
        <div class="flex flex-col gap-2">
          <RecentWidgetDocItem
            v-for="duid in recentDocs"
            :key="duid"
            :duid="duid"
            @context-menu="(event: MouseEvent) => onDocContextMenu(event, duid)" />
        </div>
      </div>

      <div class="min-h-12 w-full" />
    </div>
    <EmptyState v-else :main-icon="RecentIcon" title="You haven't worked on any tasks or docs recently" inline />
  </div>
</template>
